import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, imgUrl, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import MapView from '@/components/MapView';
import ListingCard from '@/components/ListingCard';
import { Heart, MessageSquare, MapPin, Ruler, Calendar, Droplets, Zap, Home, Trees, Flag, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import LandIntelligence from '@/components/LandIntelligence';
import ShareButtons from '@/components/ShareButtons';
import WhatsAppButton, { PhoneButton } from '@/components/WhatsAppButton';
import VerifiedBadge from '@/components/VerifiedBadge';
import OwnerBadges from '@/components/OwnerBadges';
import OwnerReviewsSection from '@/components/OwnerReviewsSection';
import { LISTING } from '@/constants/testIds';
import { useRegisterGiulioContext } from '@/context/GiulioContext';

const fmt = (n) => n != null ? new Intl.NumberFormat('it-IT').format(n) : '—';

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [showContact, setShowContact] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(r => setListing(r.data)).catch(() => nav('/esplora'));
    api.get('/listings', { params: { limit: 3 } }).then(r => setRelated(r.data.items || []));
  }, [id, nav]);

  // Registra il terreno corrente nel contesto di Giulio
  useRegisterGiulioContext(listing ? { kind: 'listing', data: listing, label: listing.title } : null);

  // Dynamic <meta> tags for social share previews (Facebook, WhatsApp, LinkedIn, Twitter)
  useEffect(() => {
    if (!listing) return;
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const ogImage = `${backend}/api/og/listing/${listing.listing_id}.png`;
    const ogTitle = `${listing.title} — AgriNexus`;
    const ogDesc = `${listing.area_hectares} ha · ${listing.location?.region || 'Italia'}${listing.price ? ` · € ${new Intl.NumberFormat('it-IT').format(listing.price)}` : ''}. Scopri il terreno su AgriNexus.`;
    const set = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    document.title = ogTitle;
    set('meta[property="og:title"]', 'content', ogTitle);
    set('meta[property="og:description"]', 'content', ogDesc);
    set('meta[property="og:image"]', 'content', ogImage);
    set('meta[property="og:image:secure_url"]', 'content', ogImage);
    set('meta[property="og:url"]', 'content', window.location.href);
    set('meta[name="twitter:title"]', 'content', ogTitle);
    set('meta[name="twitter:description"]', 'content', ogDesc);
    set('meta[name="twitter:image"]', 'content', ogImage);
    set('meta[name="description"]', 'content', ogDesc);
  }, [listing]);

  useEffect(() => {
    if (!user) return;
    api.get('/favorites').then(r => setSaved(!!r.data?.find(x => x.listing_id === id))).catch(()=>{});
  }, [user, id]);

  const toggleSave = async () => {
    if (!user) { nav('/login'); return; }
    try {
      if (saved) { await api.delete(`/favorites/${id}`); setSaved(false); toast('Rimosso dai preferiti'); }
      else { await api.post(`/favorites/${id}`); setSaved(true); toast.success('Aggiunto ai preferiti'); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendMessage = async () => {
    if (!user) { nav('/login'); return; }
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post('/messages', { receiver_id: listing.owner_id, listing_id: id, content: msg });
      toast.success('Messaggio inviato');
      setShowContact(false); setMsg('');
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const sendReport = async () => {
    if (!user) { nav('/login'); return; }
    if (!reportReason.trim()) return;
    try {
      await api.post('/reports', { target_type: 'listing', target_id: id, reason: reportReason });
      toast.success('Segnalazione inviata. La revisioneremo al più presto.');
      setShowReport(false); setReportReason('');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!listing) return <div className="p-20 text-center text-muted-foreground">Caricamento…</div>;
  const imgs = listing.images?.length ? listing.images.map(imgUrl) : ['https://images.unsplash.com/photo-1777120765956-97edeef2bc23?crop=entropy&cs=srgb&fm=jpg&q=85'];

  // JSON-LD Rich Snippets for SEO (Google/Bing rich results)
  const jsonLd = listing ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': listing.title,
    'description': listing.description || `Terreno agricolo di ${listing.area_hectares} ettari in ${listing.location?.region || 'Italia'}. Pubblicato su AgriNexus.`,
    'image': (listing.images || []).slice(0, 3),
    'brand': { '@type': 'Organization', 'name': 'AgriNexus' },
    'offers': {
      '@type': 'Offer',
      'priceCurrency': 'EUR',
      'price': listing.price || 0,
      'availability': listing.availability === 'available' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      'url': typeof window !== 'undefined' ? window.location.href : '',
    },
    'additionalProperty': [
      { '@type': 'PropertyValue', 'name': 'Ettari', 'value': listing.area_hectares },
      { '@type': 'PropertyValue', 'name': 'Regione', 'value': listing.location?.region || '' },
      { '@type': 'PropertyValue', 'name': 'Tipo', 'value': listing.listing_type === 'rent' ? 'Affitto' : 'Vendita' },
    ].filter(p => p.value),
  } : null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      {jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />}
      <Link to="/esplora" className="text-sm text-muted-foreground link-underline">← Torna ai risultati</Link>

      <div className="mt-6 grid md:grid-cols-2 gap-3">
        <img src={imgs[0]} alt="" className="w-full h-[420px] object-cover rounded-2xl md:col-span-2"/>
        {imgs.slice(1,4).map((u,i)=><img key={i} src={u} alt="" className="w-full h-[240px] object-cover rounded-2xl"/>)}
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border border-border">
              {listing.listing_type === 'rent' ? 'Affitto' : 'Vendita'}
            </span>
            {listing.owner_identity_verified && <VerifiedBadge size="sm"/>}
          </div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tighter">{listing.title}</h1>
          <div className="mt-3 flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin size={14}/> {[listing.location?.comune, listing.location?.province, listing.location?.region].filter(Boolean).join(', ')}
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat I={Ruler} l="Superficie" v={`${fmt(listing.area_hectares)} ha`}/>
            <Stat I={Calendar} l="Disponibilità" v={listing.availability}/>
            <Stat I={Droplets} l="Acqua" v={listing.features?.water ? 'Sì' : 'No'}/>
            <Stat I={Zap} l="Elettricità" v={listing.features?.electricity ? 'Sì' : 'No'}/>
          </div>

          <h2 className="mt-12 font-display text-2xl">Descrizione</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed whitespace-pre-line">{listing.description || 'Nessuna descrizione fornita.'}</p>

          <h2 className="mt-12 font-display text-2xl">Caratteristiche</h2>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              ['water','Acqua', Droplets],
              ['irrigation','Irrigazione', Droplets],
              ['electricity','Elettricità', Zap],
              ['road_access','Accesso stradale', MapPin],
              ['buildings','Edifici', Home],
              ['fenced','Recinzioni', Trees],
            ].map(([k,l,I]) => (
              <div key={k} className={`rounded-xl border p-3 text-sm flex items-center gap-2 ${listing.features?.[k] ? 'border-foreground' : 'border-border opacity-60'}`}>
                <I size={14}/> <span>{l}</span>
                <span className="ml-auto text-xs">{listing.features?.[k] ? 'Sì' : '—'}</span>
              </div>
            ))}
          </div>

          {listing.crops?.length > 0 && (<>
            <h2 className="mt-12 font-display text-2xl">Colture compatibili</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {listing.crops.map(c => <span key={c} className="text-xs px-3 py-1.5 rounded-full bg-secondary">{c}</span>)}
            </div>
          </>)}

          <h2 className="mt-12 font-display text-2xl">Posizione</h2>
          <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
            <ShieldCheck size={12}/> Posizione approssimata (±800m) per tutela della privacy del proprietario
          </div>
          <div className="mt-4">
            <MapView listings={[listing]} center={[listing.location.lat, listing.location.lng]} zoom={11} height="360px"/>
          </div>

          <div className="mt-8 flex gap-3 flex-wrap items-center">
            <ShareButtons title={listing?.title} description={`${listing?.area_hectares || ''} ha · ${listing?.location?.region || ''} · AgriNexus`}/>
            <Button variant="outline" size="sm" className="rounded-full" onClick={()=>{ if(!user){nav('/login');return;} setShowReport(true); }}>
              <Flag size={13} className="mr-2"/> Segnala annuncio
            </Button>
          </div>

          {/* AgriNexus Land Intelligence — analisi AI premium */}
          <section className="mt-12">
            <LandIntelligence listingId={id}/>
          </section>
        </div>

        <aside className="md:sticky md:top-24 h-fit rounded-2xl border border-border p-6 bg-card">
          <div className="font-display text-3xl">{listing.price ? `€ ${fmt(listing.price)}` : 'Su richiesta'}</div>
          {listing.listing_type === 'rent' && listing.price && (
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              {listing.rental_period === 'monthly' ? '/mese' : '/anno'}
            </div>
          )}
          <div className="mt-6 space-y-2">
            <Button data-testid={LISTING.contactBtn} className="w-full rounded-full h-11" onClick={() => setShowContact(true)}>
              <MessageSquare size={14} className="mr-2"/> Contatta il proprietario
            </Button>
            {(listing.contact_whatsapp || listing.contact_phone) && (
              <div className="pt-1 grid gap-2">
                {listing.contact_whatsapp && (
                  <WhatsAppButton
                    number={listing.contact_whatsapp}
                    listingId={listing.listing_id}
                    prefillMessage={`Salve, sono interessato al vostro terreno "${listing.title}" su AgriNexus (${window.location.href})`}
                    className="w-full"
                  />
                )}
                {listing.contact_phone && (
                  <PhoneButton
                    number={listing.contact_phone}
                    listingId={listing.listing_id}
                    className="w-full"
                  />
                )}
              </div>
            )}
            <Button data-testid={LISTING.saveBtn} variant="outline" className="w-full rounded-full h-11" onClick={toggleSave}>
              <Heart size={14} className={`mr-2 ${saved?'fill-foreground':''}`}/> {saved ? 'Salvato' : 'Salva'}
            </Button>
          </div>
          <div className="mt-6 pt-6 border-t border-border text-sm">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Proprietario</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{listing.owner_name}</span>
              {listing.owner_identity_verified && <VerifiedBadge size="xs"/>}
              <OwnerBadges ownerId={listing.owner_id}/>
            </div>
            <div className="text-xs text-muted-foreground mt-2">I dati di contatto restano privati. Usa la messaggistica interna in tempo reale.</div>
            <div className="text-xs text-muted-foreground mt-2">Pubblicato: {new Date(listing.created_at).toLocaleDateString('it-IT')}</div>
            <div className="text-xs text-muted-foreground">Visualizzazioni: {listing.views || 0}</div>
          </div>
        </aside>
      </div>

      {listing.owner_id && (
        <OwnerReviewsSection ownerId={listing.owner_id} ownerName={listing.owner_name}/>
      )}

      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-display text-2xl md:text-3xl mb-6">Potrebbe interessarti anche</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.filter(r => r.listing_id !== id).slice(0,3).map(l => <ListingCard key={l.listing_id} listing={l}/>)}
          </div>
        </section>
      )}

      <Dialog open={showContact} onOpenChange={setShowContact}>
        <DialogContent>
          <DialogHeader><DialogTitle>Contatta {listing.owner_name}</DialogTitle></DialogHeader>
          <Textarea placeholder="Salve, sono interessato al vostro terreno..." rows={5} value={msg} onChange={e=>setMsg(e.target.value)}/>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setShowContact(false)}>Annulla</Button>
            <Button onClick={sendMessage} disabled={busy || !msg.trim()}>{busy ? 'Invio…' : 'Invia messaggio'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent>
          <DialogHeader><DialogTitle>Segnala questo annuncio</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">Aiutaci a tenere AgriNexus una piattaforma affidabile. Descrivi il motivo:</div>
          <Textarea placeholder="Es. informazioni ingannevoli, contenuto inappropriato…" rows={4} value={reportReason} onChange={e=>setReportReason(e.target.value)}/>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setShowReport(false)}>Annulla</Button>
            <Button onClick={sendReport} disabled={!reportReason.trim()}>Invia segnalazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ I, l, v }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <I size={16} strokeWidth={1.4} className="text-muted-foreground"/>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">{l}</div>
      <div className="font-medium mt-1">{v}</div>
    </div>
  );
}
